import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import { sanityFetch } from '../../lib/sanity';
import { urlForImage } from '../../lib/sanityImage';
import { PortableText } from '@portabletext/react';

// Types
interface Post {
    _id: string;
    title: string;
    slug: { current: string };
    mainImage: any;
    excerpt: string;
    publishedAt: string;
    author: {
        name: string;
        image: any;
    };
}

interface PostDetail extends Post {
    content: any[];
}

// Styles
const styles = {
    container: {
        maxWidth: '1280px',
        px: { xs: 2, sm: 3, lg: 4 },
    },
    accentColor: '#00C2FF',
    accentBlue: '#4339F2',
};

// ============ BLOG LIST PAGE ============
export function BlogListPage() {
    const [posts, setPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPosts = async () => {
            try {
                const query = `*[_type == "post" && status == "published"] | order(publishedAt desc) {
          _id,
          title,
          slug,
          mainImage,
          excerpt,
          publishedAt,
          "author": author->{name, image}
        }`;
                const result = await sanityFetch<Post[]>(query);
                setPosts(result);
            } catch (err) {
                console.error('Error fetching posts:', err);
                setError('Failed to load blog posts');
            } finally {
                setLoading(false);
            }
        };

        fetchPosts();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            {/* Hero Section */}
            <Box sx={{ position: 'relative', overflow: 'hidden', backgroundColor: 'white' }}>
                {/* Decorative elements */}
                <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 80,
                            left: 40,
                            width: 288,
                            height: 288,
                            backgroundColor: `${styles.accentColor}1A`,
                            borderRadius: '50%',
                            filter: 'blur(48px)',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 80,
                            right: 40,
                            width: 384,
                            height: 384,
                            backgroundColor: `${styles.accentBlue}1A`,
                            borderRadius: '50%',
                            filter: 'blur(48px)',
                        }}
                    />
                </Box>

                <Container sx={{ ...styles.container, position: 'relative', pt: 10, pb: 12 }}>
                    <Grid container spacing={6} alignItems="center">
                        <Grid item xs={12} lg={6}>
                            <Box sx={{ maxWidth: '42rem' }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 2,
                                            background: `linear-gradient(to right, ${styles.accentColor}, ${styles.accentBlue})`,
                                        }}
                                    />
                                    <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                        Soopra Blog
                                    </Typography>
                                </Box>

                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontSize: { xs: '2rem', sm: '3rem', md: '3.75rem' },
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        mb: 3,
                                        lineHeight: 1.1,
                                    }}
                                >
                                    The Future of{' '}
                                    <Box
                                        component="span"
                                        sx={{
                                            background: `linear-gradient(to right, ${styles.accentColor}, ${styles.accentBlue})`,
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            color: 'transparent',
                                        }}
                                    >
                                        AI-Powered
                                    </Box>{' '}
                                    Personal Growth
                                </Typography>

                                <Typography sx={{ fontSize: '1.25rem', color: 'text.secondary', mb: 4, lineHeight: 1.6 }}>
                                    Discover insights about AI personalities, expert knowledge sharing, and the revolution in personalized learning. Stay updated with Soopra's latest innovations in AI technology.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} lg={6}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    aspectRatio: '1/1',
                                }}
                            >
                                <Box
                                    component="img"
                                    src="https://wevcmvkclihorrkofawh.supabase.co/storage/v1/object/public/blog/blog-hero.png"
                                    alt="AI Personal Growth"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        borderRadius: 4,
                                        boxShadow: 6,
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Blog Posts Grid */}
            <Container sx={{ ...styles.container, py: 8 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 6 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Latest Articles
                    </Typography>
                </Box>

                {posts.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography color="text.secondary">No blog posts yet. Check back soon!</Typography>
                    </Box>
                ) : (
                    <Grid container spacing={4}>
                        {posts.map((post) => (
                            <Grid item xs={12} md={6} lg={4} key={post._id}>
                                <Link to={`/blog/${post.slug.current}`} style={{ textDecoration: 'none' }}>
                                    <Box
                                        sx={{
                                            backgroundColor: 'background.paper',
                                            borderRadius: 2,
                                            boxShadow: 2,
                                            overflow: 'hidden',
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: 4,
                                            },
                                        }}
                                    >
                                        {post.mainImage && (
                                            <Box sx={{ position: 'relative', height: 192, width: '100%' }}>
                                                <Box
                                                    component="img"
                                                    src={urlForImage(post.mainImage).url()}
                                                    alt={post.title}
                                                    sx={{
                                                        width: '100%',
                                                        height: '100%',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        <Box sx={{ p: 3 }}>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 1,
                                                    color: 'text.primary',
                                                    '&:hover': { color: '#0066FF' },
                                                }}
                                            >
                                                {post.title}
                                            </Typography>
                                            {post.excerpt && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        mb: 2,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                >
                                                    {post.excerpt}
                                                </Typography>
                                            )}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {post.author?.image && (
                                                    <Box
                                                        component="img"
                                                        src={urlForImage(post.author.image).url()}
                                                        alt={post.author.name}
                                                        sx={{
                                                            width: 32,
                                                            height: 32,
                                                            borderRadius: '50%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                )}
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {post.author?.name}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    •
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {new Date(post.publishedAt).toLocaleDateString('en-US', {
                                                        month: 'long',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                    })}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Link>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>
        </Box>
    );
}

// ============ BLOG POST PAGE ============
export function BlogPostPage() {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<PostDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchPost = async () => {
            if (!slug) return;

            try {
                const query = `*[_type == "post" && slug.current == $slug][0]{
          _id,
          title,
          slug,
          mainImage,
          content,
          excerpt,
          publishedAt,
          "author": author->{name, image}
        }`;
                const result = await sanityFetch<PostDetail | null>(query, { slug });
                if (!result) {
                    setError('Post not found');
                } else {
                    setPost(result);
                }
            } catch (err) {
                console.error('Error fetching post:', err);
                setError('Failed to load blog post');
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !post) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Typography color="error">{error || 'Post not found'}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
            <Container sx={{ maxWidth: '768px', px: { xs: 2, sm: 3 }, py: 6 }}>
                <Link to="/blog" style={{ textDecoration: 'none' }}>
                    <Typography
                        sx={{
                            color: 'text.secondary',
                            mb: 4,
                            display: 'inline-flex',
                            alignItems: 'center',
                            '&:hover': { color: 'primary.main' },
                        }}
                    >
                        ← Back to Blog
                    </Typography>
                </Link>

                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 700,
                        mb: 2,
                        fontSize: { xs: '2rem', md: '2.5rem' },
                    }}
                >
                    {post.title}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    {post.author?.image && (
                        <Box
                            component="img"
                            src={urlForImage(post.author.image).url()}
                            alt={post.author.name}
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                objectFit: 'cover',
                            }}
                        />
                    )}
                    <Box>
                        <Typography sx={{ fontWeight: 500 }}>{post.author?.name}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {new Date(post.publishedAt).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                            })}
                        </Typography>
                    </Box>
                </Box>

                {post.mainImage && (
                    <Box sx={{ position: 'relative', height: 400, width: '100%', mb: 4 }}>
                        <Box
                            component="img"
                            src={urlForImage(post.mainImage).url()}
                            alt={post.title}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: 2,
                            }}
                        />
                    </Box>
                )}

                <Box
                    sx={{
                        '& p': { mb: 2, lineHeight: 1.8 },
                        '& h2': { mt: 4, mb: 2, fontWeight: 700 },
                        '& h3': { mt: 3, mb: 2, fontWeight: 600 },
                        '& ul, & ol': { pl: 3, mb: 2 },
                        '& li': { mb: 1 },
                        '& a': { color: 'primary.main' },
                        '& blockquote': {
                            borderLeft: '4px solid',
                            borderColor: 'primary.main',
                            pl: 3,
                            py: 1,
                            my: 3,
                            fontStyle: 'italic',
                            backgroundColor: 'action.hover',
                        },
                    }}
                >
                    {post.content && <PortableText value={post.content} />}
                </Box>
            </Container>
        </Box>
    );
}

export default BlogListPage;
